import { del, get, put, post, patch } from "@/services/axiosUitils";
import {
  DropDownResponse,
  GoldProductResponse,
  GetProductsResponse,
  DeliveryMasterResponse,
  DeliveryMasterData,
  SettlementListResponse,
  MetalPriceResponse,
  MetalPricePayload,
  GoldCommissionUpdateResponse,
} from "@/utils/types/Gold";

const base = "/physical_gold/product-master/";

class GoldService {
  getDashboard = async (): Promise<any> => {
    return await get(`${base}dashboard`);
  };

  getProducts = async (
    page: number = 1,
    pageSize: number = 10
  ): Promise<GetProductsResponse> => {
    return await get(`${base}?page=${page}&page_size=${pageSize}`);
  };

  createProduct = async (data: any): Promise<GoldProductResponse> => {
    return await post(`${base}`, data);
  };

  getProductById = async (id: string): Promise<GoldProductResponse> => {
    return await get(`${base}${id}/`);
  };

  updateProduct = async (
    id: string,
    data: any
  ): Promise<GoldProductResponse> => {
    return await put(`${base}${id}/`, data);
  };

  getPurityChoices = async (): Promise<DropDownResponse> => {
    return await get(`${base}purity-choices/`);
  };

  getMetalChoices = async (): Promise<DropDownResponse> => {
    return await get(`${base}metal-choices/`);
  };

  getShapeChoices = async (): Promise<DropDownResponse> => {
    return await get(`${base}shape-choices/`);
  };

  deleteProduct = async (id: string): Promise<any> => {
    return await del(`${base}${id}/`);
  };

  exportExcel = async (productIds: string[]): Promise<Blob> => {
    const ids = productIds.length > 0 ? `${productIds.join(",")},` : "";
    return await get(`${base}export-excel/?product_ids=${ids}`, {
      responseType: "blob",
    });
  };

  importExcel = async (formData: FormData): Promise<any> => {
    return await post(`${base}import-excel/`, formData);
  };

  // Delivery Master APIs
  getDeliveryMasters = async (): Promise<any> => {
    return await get(`/physical_gold/pincode-master/`);
  };

  createDeliveryMaster = async (data: any): Promise<any> => {
    return await post(`/physical_gold/pincode-master/`, data);
  };

  getDeliveryMasterById = async (id: string): Promise<DeliveryMasterData> => {
    return await get(`/physical_gold/pincode-master/${id}/`);
  };

  updateDeliveryMaster = async (id: string, data: any): Promise<any> => {
    return await put(`/physical_gold/pincode-master/${id}/`, data);
  };

  deleteDeliveryMaster = async (id: string): Promise<any> => {
    return await del(`/physical_gold/pincode-master/${id}/`);
  };

  importDeliveryMasters = async (data: any): Promise<any> => {
    return await post(`/physical_gold/pincode-master/import_csv/`, data);
  };

  listDeliveryMasters = async (
    courier_partner?: string | number,
    page: number = 1,
    pageSize: number = 10,
    search: string = ""
  ): Promise<DeliveryMasterResponse> => {
    let url = `/physical_gold/pincode-master/?page=${page}&page_size=${pageSize}`;
    if (courier_partner && courier_partner !== "ALL") {
      url += `&priority=${courier_partner}`;
    }
    if (search) {
      url += `&search=${search}`;
    }
    return await get(url);
  };

  // Order APIs
  getOrders = async (
    page: number = 1,
    pageSize: number = 10,
    search: string = "",
    status: string = ""
  ): Promise<any> => {
    let url = `/physical_gold/order/all/?page=${page}&page_size=${pageSize}`;
    if (search) {
      url += `&search=${search}`;
    }
    if (status) {
      url += `&status=${status}`;
    }
    return await get(url);
  };

  getOrderById = async (id: string): Promise<any> => {
    return await get(`/physical_gold/order/${id}/`);
  };

  /** Partial update (e.g. MMTC order id). Adjust payload keys to match backend. */
  updateOrder = async (
    orderId: string,
    data: Record<string, unknown>
  ): Promise<any> => {
    return await patch(`/physical_gold/order/${orderId}/`, data);
  };

  uploadInvoice = async (orderId: string, formData: FormData): Promise<any> => {
    return await post(
      `/physical_gold/order/${orderId}/upload-invoice/`,
      formData
    );
  };

  settlementList = async (
    page: number = 1,
    pageSize: number = 10,
    search: string = "",
    status: string = ""
  ): Promise<SettlementListResponse> => {
    let url = `/physical_gold/settlement-report/?page=${page}&page_size=${pageSize}`;
    if (search) {
      url += `&search=${search}`;
    }
    if (status) {
      url += `&status=${status}`;
    }
    return await get(url);
  };

  settlementListById = async (id: string): Promise<SettlementListResponse> => {
    const url = `/physical_gold/settlement-report/${id}/`;
    return await get(url);
  };

  updateSettlementById = async (
    id: string,
    data: {
      payment_method?: string | null;
      payment_status?: string | null;
      settlement_status?: string | null;
    }
  ): Promise<any> => {
    const url = `/physical_gold/settlement-report/${id}/`;
    return await patch(url, data);
  };

  // Metal price / rate APIs (Pricing Control)
  getPricesList = async (): Promise<{ data: any[] }> => {
    const res = await get("/physical_gold/prices/");
    const data = Array.isArray(res) ? res : (res?.data ?? res?.results ?? []);
    return { data: Array.isArray(data) ? data : [] };
  };

  createPrice = async (payload: {
    purity: string;
    price_per_gram: number;
  }): Promise<any> => {
    return await post("/physical_gold/prices/", payload);
  };

  GetMetalPrice = async (
    page: number = 1,
    pageSize: number = 10
  ): Promise<MetalPriceResponse> => {
    return await get(
      `/physical_gold/metal-prices/?page=${page}&page_size=${pageSize}`
    );
  };

  createMetalPrice = async (payload: MetalPricePayload): Promise<any> => {
    return await post("/physical_gold/metal-prices/", payload);
  };

  // Commission APIs

  updateCommissionById = async (
    id: string,
    payload: {
      metal: string;
      commission_percentage: string;
    }
  ): Promise<GoldCommissionUpdateResponse> => {
    return await put(`/physical_gold/commission-percentages/${id}/`, payload);
  };

  getCommission = async (
    page: number = 1,
    pageSize: number = 10
  ): Promise<GoldCommissionUpdateResponse> => {
    return await get(
      `/physical_gold/commission-percentages/?page=${page}&page_size=${pageSize}`
    );
  };
}
export default new GoldService();
